import { addDays, addMonths, addYears, format, startOfMonth } from 'date-fns';
import { getStatusMetadata } from '@/features/Vacations/vacations.definitions';
import type {
    HistoryRequestMilestone,
    HistoryRequestRow,
} from '@/features/Vacations/models/vacationHistoryReport.types';

const iso = (date: Date) => format(date, 'yyyy-MM-dd');

function buildMilestones(
    today: Date,
    segments: Array<{ order: number; start: Date; end: Date }>
): HistoryRequestMilestone[] {
    const todayIso = iso(today);
    const flow: HistoryRequestMilestone[] = [
        { kind: 'requested', occurredAt: addDays(today, -40), actorEmail: null },
        { kind: 'managerApproved', occurredAt: addDays(today, -36), actorEmail: 'gestor@rarolabs.com.br' },
        { kind: 'clientApproved', occurredAt: addDays(today, -34), actorEmail: 'cliente@exemplo.com.br' },
        { kind: 'dpApproved', occurredAt: addDays(today, -31), actorEmail: 'dp@rarolabs.com.br' },
    ];
    const stages = segments.flatMap(({ order, start, end }): HistoryRequestMilestone[] => [
        { kind: 'started', occurredAt: start, actorEmail: null, segmentOrder: order, isProjected: iso(start) > todayIso },
        { kind: 'completed', occurredAt: end, actorEmail: null, segmentOrder: order, isProjected: iso(end) > todayIso },
    ]);
    return [...flow, ...stages];
}

function buildPeriod(start: Date, days: number) {
    return { start, end: addDays(start, days - 1), days };
}

export function buildHistoryDemoRows(today: Date): HistoryRequestRow[] {
    const month = startOfMonth(today);
    const scheduled = getStatusMetadata('Scheduled');
    const acquisitionStart = addYears(month, -2);

    const anaFirst = buildPeriod(addDays(month, 5), 15);
    const anaSecond = buildPeriod(addDays(addMonths(month, 2), 5), 15);
    const brunoOnly = buildPeriod(addDays(month, 12), 20);
    const carla = buildPeriod(addDays(month, 19), 10);

    const ana: HistoryRequestRow = {
        requestId: 'demo-history-ana',
        collaborator: { id: 'demo-history-ana', name: 'Ana Exemplo', email: 'ana.exemplo@rarolabs.com.br' },
        segments: [
            { order: 1, startDate: iso(anaFirst.start), endDate: iso(anaFirst.end), days: anaFirst.days },
            { order: 2, startDate: iso(anaSecond.start), endDate: iso(anaSecond.end), days: anaSecond.days },
        ],
        totalDays: 30,
        hasApprovedSell: false,
        sellDays: 0,
        currentStatus: scheduled.label,
        currentStatusClass: scheduled.className,
        milestones: buildMilestones(today, [
            { order: 1, start: anaFirst.start, end: anaFirst.end },
            { order: 2, start: anaSecond.start, end: anaSecond.end },
        ]),
        isManual: false,
        periodAllocations: [
            {
                periodId: 'demo-period',
                startDate: iso(acquisitionStart),
                endDate: iso(addDays(addYears(acquisitionStart, 1), -1)),
                concessiveDeadline: iso(addYears(acquisitionStart, 2)),
                days: 30,
                isAccruing: false,
            },
        ],
        requestStatus: 'Scheduled',
    };

    const bruno: HistoryRequestRow = {
        requestId: 'demo-history-bruno',
        collaborator: { id: 'demo-history-bruno', name: 'Bruno Exemplo', email: 'bruno.exemplo@rarolabs.com.br' },
        segments: [{ order: 1, startDate: iso(brunoOnly.start), endDate: iso(brunoOnly.end), days: brunoOnly.days }],
        totalDays: 20,
        hasApprovedSell: true,
        sellDays: 10,
        currentStatus: scheduled.label,
        currentStatusClass: scheduled.className,
        milestones: buildMilestones(today, [{ order: 1, start: brunoOnly.start, end: brunoOnly.end }]),
        isManual: false,
        requestStatus: 'Scheduled',
    };

    const manual: HistoryRequestRow = {
        requestId: null,
        collaborator: { id: 'demo-history-carla', name: 'Carla Exemplo', email: 'carla.exemplo@rarolabs.com.br' },
        segments: [{ order: 1, startDate: iso(carla.start), endDate: iso(carla.end), days: carla.days }],
        totalDays: carla.days,
        hasApprovedSell: false,
        sellDays: 0,
        currentStatus: 'Lançamento manual',
        currentStatusClass: 'bg-gray-100 text-gray-700',
        milestones: [],
        isManual: true,
        manualIndex: 0,
    };

    return [ana, bruno, manual];
}
